import {
  Filter
} from '../common/filter';
import {
  ticksToHours,
  WEEKDAYS
} from '../common/datetime';
import {
  PATH_STRING_SEPARATOR,
} from '../common/config';
export class ChartBuilder {

  constructor(
    aggregate,
    filter,
    chartType,
    chartAreaIsStacked,
    model,
  ) {
    this.aggregate = aggregate;
    this.filter = filter;
    this.chartType = chartType;
    this.chartAreaIsStacked = chartAreaIsStacked;
    this.model = model;
  }

  reset() {
    this.xData = [];
    this.yData = [];
    this.legendData = [];
    this.filterValues = [];
    this.filterYValues = [];
  }

  build() {
    this.reset();
    if (!this.aggregate ||
      !this.aggregate.filter ||
      !this.aggregate.filter.drilldown ||
      !this.aggregate.filter.drilldown.length ||
      !this.aggregate.cells ||
      !this.aggregate.cells.length
    ) {
      return;
    }
    const isSingleDim = this.filter.drilldown.length === 1;
    if (isSingleDim && !this.shouldSortDimension(
        this.filter.drilldown[0])) {
      this.legendData = [''];
      this.buildChartSingleDim();
    } else {
      this.buildChartData();
    }
  }

  /**
   * single dimension case
   */
  buildChartSingleDim() {
    const drill = this.filter.drilldown[0];
    this.filterValues = this.aggregate.cells.map((cell) => {
      return this.model.getCellFilter(drill, cell);
    });
    this.xData = this.filterValues.map((filter) => filter.label || '');
    this.yData = [{
      name: '',
      type: this.chartType,
      stack: this.chartAreaIsStacked,
      data: this.aggregate.cells.map(cell => this.getMeasureValue(cell)),
    }, ];
  }

  buildChartData() {
    this.yKeysSet = {};
    const data = this.buildData();
    if (!data) {
      return;
    }

    this.buildXData(data);
    this.buildYData(data);
  }

  get isSingleDim() {
    return this.filter.drilldown.length === 1;
  }

  /**
   * xData builder
   * @returns {undefined}
   */
  buildXData(data) {
    const xDim = this.filter.drilldown[0];
    if (Filter.isTimeDimension(xDim)) {
      this.buildTimeLine(xDim, data);
      this.sortKeys(xDim, this.xData, data);

    } else {
      this.xData = Object.keys(data);
      this.sortKeys(xDim, this.xData, data);
      this.filterValues = this.xData.map((key) => ({
        value: data[key].value,
        label: key,
        dimension: xDim,
      }));
    }
  }

  /**
   * yData builder
   */

  limitString(str) {
    // const maxLen = 15;
    // if (str.length && str.length > maxLen) return `${str.substring(0, maxLen)}...`;
    return str;
  }

  buildYData(data) {
    if (this.isSingleDim) {
      this.legendData = [''];
      const values = this.xData.map((key) => (data[key] || {
        data: 0
      }).data);
      this.yData = [{
        name: '',
        type: this.chartType,
        stack: this.chartAreaIsStacked,
        data: values,
      }, ];
    } else {
      const yDim = this.filter.drilldown[1];
      const yKeys = Object.keys(this.yKeysSet);
      this.sortKeys(yDim, yKeys, this.yKeysSet);
      this.legendData = yKeys;
      this.filterYValues = yKeys.map((key) => {
        return this.yKeysSet[key];
      });
      const yValues = this.xData.map((key) => {
        return (data[key] || {
          data: {}
        }).data;
      });
      this.yData = yKeys.map((yKey) => {
        let accData = 0;
        const values = yValues.map(yValue => {
          const val = yValue[yKey] || 0;
          accData += val;
          return val;
        });
        // this.yKeysSet[yKey].accData = accData;
        return {
          name: yKey,
          type: this.chartType,
          stack: this.chartAreaIsStacked,
          data: values,
          accData: accData,
        };
      });
      // this.sortKeys(yDim, yKeys, this.yKeysSet);

    }
  }

  /**
   * aggregate data object
   * @returns {undefined | Object.<string, {value: *, data: Object.<string, *>}>}
   */
  buildData() {
    const data = {};

    const cells = this.aggregate.cells;
    for (let i = 0; i < cells.length; i++) {

      const cell = cells[i];
      const size = this.getMeasureValue(cell);
      const drills = this.filter.drilldown;
      const xFilter = this.model.getCellFilter(drills[0], cell);
      const xlabel = xFilter.label || xFilter.value;
      if (drills.length === 1) {
        data[xlabel] = {
          value: xFilter.value,
          data: size,
          accData: size,
        };
        continue;
      }
      const yFilter = this.model.getCellFilter(drills[1], cell);
      let xFilterData = data[xlabel];
      if (!xFilterData) {
        xFilterData = {
          value: xFilter.value,
          data: {},
          filter: xFilter,
          accData: 0,
        };
        data[xlabel] = xFilterData;
      }
      const ylabel = this.limitString(yFilter.label || yFilter.value);
      let value = size + (xFilterData.data[ylabel] || 0);
      xFilterData.data[ylabel] = value;
      xFilterData.accData += value;
      value = size + (this.yKeysSet[ylabel] && this.yKeysSet[ylabel].accData ?
        this.yKeysSet[ylabel].accData : 0);
      this.yKeysSet[ylabel] = yFilter;
      this.yKeysSet[ylabel].accData = value;
    }

    return data;
  }

  /**
   * build linear time-line
   */
  buildTimeLine(dim, data) {
    const startDate = typeof this.aggregate.filter.timeFrom === 'string' ?
      new Date(this.aggregate.filter.timeFrom) : this.aggregate.filter.timeFrom;
    const endDate = typeof this.aggregate.filter.timeTo === 'string' ?
      new Date(this.aggregate.filter.timeTo) : this.aggregate.filter.timeTo;

    let count = endDate.getTime() - startDate.getTime();
    let timeGetter;
    let timeSetter;
    const dimParts = this.model.getDimensionParts(dim);
    const level = dimParts.hierarchy.levels.length;
    const quantity = dimParts.hierarchy.levels[level - 1];
    switch (quantity) {
      case 'minute':
        timeGetter = Date.prototype.getMinutes;
        timeSetter = Date.prototype.setMinutes;
        count = Math.floor(count / (60 * 1000));
        break;
      case 'hour':
        timeGetter = Date.prototype.getHours;
        timeSetter = Date.prototype.setHours;
        count = Math.floor(count / (60 * 60 * 1000));
        break;
      case 'day':
        timeGetter = Date.prototype.getDate;
        timeSetter = Date.prototype.setDate;
        count = Math.floor(count / (24 * 60 * 60 * 1000));
        break;
      case 'month':
        timeGetter = Date.prototype.getMonth;
        timeSetter = Date.prototype.setMonth;
        count = 12 * (endDate.getFullYear() - startDate.getFullYear()) +
          endDate.getMonth() - startDate.getMonth();
        break;
      case 'year':
        timeGetter = Date.prototype.getFullYear;
        timeSetter = Date.prototype.setFullYear;
        count = endDate.getYear() - startDate.getYear();
        break;
      default:
        console.error(
          quantity,
          'is unknown time line quantity, use day by default',
        );
        timeGetter = Date.prototype.getDate;
        timeSetter = Date.prototype.setDate;
        count = Math.floor(count / (24 * 60 * 60 * 1000));
        break;
    }
    count++;

    // trim left

    const date = new Date(startDate.getTime());
    while (!data[this.getTimeLabel(date, level)] && count) {
      timeSetter.call(date, timeGetter.call(date) + 1);
      --count;
    }

    // trim right

    const edate = new Date(endDate.getTime());
    while (count && !data[this.getTimeLabel(edate, level)]) {
      timeSetter.call(edate, timeGetter.call(edate) - 1);
      --count;
    }

    // build

    this.xData = new Array(count);
    for (let i = 0; i < count; ++i) {
      this.xData[i] = this.getTimeLabel(date, level);
      const label = this.xData[i];
      this.filterValues[i] = {
        dimension: dim,
        label,
        value: this.getTimeValue(date, level),
      };
      timeSetter.call(date, timeGetter.call(date) + 1);
    }
  }

  /**
   * convert date to string like '2016,10,11'
   * @param {Date} date
   * @param {number} level
   */
  getTimeValue(date, level) {
    return Filter.valueForTime(date, level, true);
  }

  /**
   * format time regarding dimension levels
   * @param {Date} date
   * @param {number} level
   */
  getTimeLabel(date, level) {
    return Filter.labelForTime(date, level);
  }

  /**
   * sort keys array
   * @param {string} dim dimension string
   * @param {string[]} keys keys array to sort
   * @param {Object.<string, {value: *}>} values
   */
  // sortKeys1(dim, keys, values) {
  //   if (Filter.isTimeDimension(dim)) {
  //     // Convert time values to number,
  //     // in order to optimize sorting
  //     // values = this.filterValues;
  //     keys.forEach(key => {
  //
  //       const value = values[key].value;
  //       // values[key].timeValue = value.split(',')
  //       values[key].timeValue = value.split(PATH_STRING_SEPARATOR)
  //         .reduce((prev, cur) => 100 * prev + Number(cur), 0);
  //     });
  //   } else if (dim === 'agent_time@weekday:weekday') {
  //     keys.forEach((key) => {
  //       values[key].timeValue = WEEKDAYS.indexOf(key);
  //     });
  //   } else if (dim === 'agent_time@hour:hour') {
  //     keys.forEach((key) => {
  //       values[key].timeValue = Number(key);
  //     });
  //   }
  //   keys.sort(this.compareFilter(dim, values));
  // }


  sortKeys(dim, keys, values) {
    if (Filter.isTimeDimension(dim)) {
      // Convert time values to number,
      // in order to optimize sorting
      // values = this.filterValues;
      keys.forEach(key => {
        let valueFata = values[key];
        if (!valueFata) valueFata = this.filterValues.find(fv => fv.label === key);
        const value = valueFata.value;
        // values[key].timeValue = value.split(',')
        valueFata.timeValue = value.split(PATH_STRING_SEPARATOR)
          .reduce((prev, cur) => 100 * prev + Number(cur), 0);
      });
    } else if (dim === 'agent_time@weekday:weekday') {
      keys.forEach((key) => {
        values[key].timeValue = WEEKDAYS.indexOf(key);

      });
    } else if (dim === 'agent_time@hour:hour') {
      keys.forEach((key) => {
        values[key].timeValue = Number(key);
      });
    }
    keys.sort(this.compareFilter(dim, values));
  }

  /**
   * returns comparator for sorting
   * @param {string} dim dimension string
   * @param {Object.<string, {value: *}>} values
   * @returns {function} | {undefined}
   */
  compareFilter(dim, values) {
    const sortType = this.shouldSortDimension(dim);
    const isDataEquial = (a, b) => (values[a] && values[a].accData ? values[a].accData : 0) ===
      (values[b] && values[b].accData ? values[b].accData : 0);

    // const ascDim_Time = (a, b) => values[a].timeValue - values[b].timeValue;
    const ascDim_Time = (a, b) => {
      let aVal = values[a];
      if (!aVal) aVal = this.filterValues.find(fv => fv.label === a);
      let bVal = values[b];
      if (!bVal) bVal = this.filterValues.find(fv => fv.label === b);

      aVal.timeValue - bVal.timeValue;
    };
    const descDim_Time = (a, b) => ascDim_Time(b, a);
    const ascDim = (a, b) => a.toLowerCase().localeCompare(b.toLowerCase());
    const descDim = (a, b) => ascDim(b, a);

    const ascMeasure = (a, b) => (values[a] && values[a].accData ? values[a].accData : 0) -
      (values[b] && values[b].accData ? values[b].accData : 0);
    const descMeasure = (a, b) => ascMeasure(b, a);

    const ascMeasure_ascDim = (a, b) => {
      if (isDataEquial(a, b)) return ascDim(a, b);
      return ascMeasure(a, b);
    };
    const descMeasure_ascDim = (a, b) => {
      if (isDataEquial(a, b)) return ascDim(a, b);
      return descMeasure(a, b);
    };
    const ascMeasure_descDim = (a, b) => descMeasure_ascDim(b, a);
    const descMeasure_descDim = (a, b) => ascMeasure_ascDim(b, a);

    const ascMeasure_ascDim_Time = (a, b) => {
      if (isDataEquial(a, b)) return ascDim_Time(a, b);
      return ascMeasure(a, b);
    };
    const descMeasure_ascDim_Time = (a, b) => {
      if (isDataEquial(a, b)) return ascDim_Time(a, b);
      return descMeasure(a, b);
    };

    const ascMeasure_descDim_Time = (a, b) => descMeasure_ascDim_Time(b, a);
    const descMeasure_descDim_Time = (a, b) => ascMeasure_ascDim_Time(b, a);

    const ascDim_ascMeasure = (a, b) => a === b ? ascMeasure(a, b) : ascDim(a, b);
    const descDim_ascMeasure = (a, b) => a === b ? ascMeasure(a, b) : descDim(a, b);
    const ascDim_descMeasure = (a, b) => a === b ? descMeasure(a, b) : ascDim(a, b);
    const descDim_descMeasure = (a, b) => a === b ? descMeasure(a, b) : descDim(a, b);

    const ascDim_ascMeasure_Time = (a, b) => values[a].timeValue === values[b].timeValue ? ascMeasure(a, b) : ascDim_Time(a, b);
    const descDim_ascMeasure_Time = (a, b) => values[a].timeValue === values[b].timeValue ? ascMeasure(a, b) : descDim_Time(a, b);
    const ascDim_descMeasure_Time = (a, b) => values[a].timeValue === values[b].timeValue ? descMeasure(a, b) : ascDim_Time(a, b);
    const descDim_descMeasure_Time = (a, b) => values[a].timeValue === values[b].timeValue ? descMeasure(a, b) : descDim_Time(a, b);


    const compareRules = {
      ascDim: ascDim,
      descDim: descDim,
      ascDim_Time: ascDim_Time,
      descDim_Time: descDim_Time,

      ascMeasure: ascMeasure,
      descMeasure: descMeasure,
      ascMeasure_Time: ascMeasure,
      descMeasure_Time: descMeasure,

      ascMeasure_ascDim: ascMeasure_ascDim,
      descMeasure_ascDim: descMeasure_ascDim,
      ascMeasure_descDim: ascMeasure_descDim,
      descMeasure_descDim: descMeasure_descDim,

      ascMeasure_ascDim_Time: ascMeasure_ascDim_Time,
      descMeasure_ascDim_Time: descMeasure_ascDim_Time,
      ascMeasure_descDim_Time: ascMeasure_descDim_Time,
      descMeasure_descDim_Time: descMeasure_descDim_Time,

      ascDim_ascMeasure: ascDim_ascMeasure,
      descDim_ascMeasure: descDim_ascMeasure,
      ascDim_descMeasure: ascDim_descMeasure,
      descDim_descMeasure: descDim_descMeasure,

      ascDim_ascMeasure_Time: ascDim_ascMeasure_Time,
      descDim_ascMeasure_Time: descDim_ascMeasure_Time,
      ascDim_descMeasure_Time: ascDim_descMeasure_Time,
      descDim_descMeasure_Time: descDim_descMeasure_Time,

      '': (a, b) => 0,
    };
    // console.log(sortType, compareRules[sortType]);
    return compareRules[sortType] || compareRules[''];
  }

  getOrderColumnName(drill) {
    const dimension = this.model.getDimensionParts(drill);
    return `${dimension.dimension.name}.${dimension.level.label_attribute}`;
  }

  shouldSortDimension(dim) {
    let orderDir = '';
    const oName = this.getOrderColumnName(dim);

    if (this.filter && this.filter.orders.length) {
      this.filter.orders.map((d, i) => {
        if (d === oName) {
          if (orderDir) orderDir += '_';
          orderDir += (this.filter.orderDirs[i] + 'Dim');
        }
        if (d === 'measure') {
          if (orderDir) orderDir += '_';
          orderDir += (this.filter.orderDirs[i] + 'Measure');
        }
      });
      // const i = this.filter.orders.findIndex(d => d === oName);
      // if (i !== -1) orderDir = this.filter.orderDirs[i];
    }

    if (Filter.isTimeDimension(dim) || dim === 'agent_time@weekday:weekday' ||
      dim === 'agent_time@hour:hour') orderDir += '_Time';
    return orderDir;
  }

  getMeasureValue(cell) {
    const name = this.filter.measure;
    const val = typeof(cell) === 'object' ? cell && cell[name] || 0 : cell;
    if (name === 'activity_time_sum') {
      return val / ticksToHours;
    } else if (name === 'call_duration_sum') {
      return Math.floor(val / 60);
    } else {
      return val;
    } //bytesToHuman(val, true );
  }
}



// WEBPACK FOOTER //
// ./src/stat/chart-builder.js